import { useCallback, useEffect, useState } from "react";

const isBrowser = typeof window !== "undefined";

/**
 * useLocalStorage — small JSON-serialised localStorage wrapper.
 *
 * Security scope: ClearPath is a non-functional prototype. The only values
 * persisted via this hook are simulated demo data (accessibility prefs,
 * trusted contact names, synthetic vitals readings, typed reply drafts, paid
 * bill ids). No credentials, no PHI, no auth tokens are ever stored. A
 * production app would route any sensitive data through httpOnly cookies or
 * an encrypted server-side store instead.
 */

function read(key, fallback) {
    if (!isBrowser) return fallback;
    try {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return fallback;
        return JSON.parse(raw);
    } catch (err) {
        // Most commonly: corrupt JSON or disabled storage (private mode).
        if (typeof console !== "undefined") {
            console.warn(`[useLocalStorage] read("${key}") failed:`, err);
        }
        return fallback;
    }
}

function write(key, value) {
    if (!isBrowser) return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
        // Most commonly: quota exceeded or private mode.
        if (typeof console !== "undefined") {
            console.warn(`[useLocalStorage] write("${key}") failed:`, err);
        }
    }
}

export default function useLocalStorage(key, initialValue) {
    const [value, setValue] = useState(() => read(key, initialValue));

    useEffect(() => {
        write(key, value);
    }, [key, value]);

    const reset = useCallback(() => {
        setValue(initialValue);
        if (isBrowser) {
            try {
                window.localStorage.removeItem(key);
            } catch (err) {
                if (typeof console !== "undefined") {
                    console.warn(
                        `[useLocalStorage] reset("${key}") failed:`,
                        err,
                    );
                }
            }
        }
    }, [key, initialValue]);

    return [value, setValue, reset];
}
